/**
 * NotificationContext
 *
 * Handles Firebase Cloud Messaging push notification permission flow.
 * - On login: requests permission, gets FCM token, registers with backend
 * - On logout: unregisters token from backend
 * - Shows a toast-style banner asking for permission if not yet granted
 *
 * Firebase Setup (one-time):
 * 1. Go to console.firebase.google.com → your project → Project Settings → General
 * 2. Add a Web App, copy the firebaseConfig object
 * 3. Create REACT_APP_FIREBASE_* env vars (see frontend/.env.example)
 * 4. In Project Settings → Cloud Messaging → Web Push certificates → Generate key pair
 * 5. Copy the VAPID public key to REACT_APP_FIREBASE_VAPID_KEY
 * 6. Place firebase-messaging-sw.js in frontend/public/ (see below)
 */
import { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { paymentAPI } from '../services/api';

const NotificationContext = createContext(null);

// ── Config ────────────────────────────────────────────────────────────────────
// These come from your Firebase project settings.
// Fill them in frontend/.env:
//   REACT_APP_FIREBASE_API_KEY=...
//   REACT_APP_FIREBASE_AUTH_DOMAIN=...
//   REACT_APP_FIREBASE_PROJECT_ID=...
//   REACT_APP_FIREBASE_MESSAGING_SENDER_ID=...
//   REACT_APP_FIREBASE_APP_ID=...
//   REACT_APP_FIREBASE_VAPID_KEY=...

const FIREBASE_CONFIG = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Detect whether Firebase is configured (all required env vars present)
const isFirebaseConfigured = () =>
  FIREBASE_CONFIG.apiKey && FIREBASE_CONFIG.projectId &&
  FIREBASE_CONFIG.apiKey !== 'your_api_key' && VAPID_KEY;

// ── Provider ──────────────────────────────────────────────────────────────────
export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const registeredToken = useRef(null);
  const messagingRef    = useRef(null);

  const initFirebase = useCallback(async () => {
    if (!isFirebaseConfigured()) {
      // Not configured — skip silently (notifications just won't work)
      return;
    }

    // Check browser support
    // Safari < 16.4 and some iOS browsers don't support Notifications
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    // Extra iOS Safari check — Notification exists but permission query can throw
    try { void Notification.permission; } catch { return; }

    // Don't re-init if already done
    if (messagingRef.current) return;

    try {
      // Dynamic import so the app doesn't crash when Firebase isn't configured
      const { initializeApp, getApps } = await import('firebase/app');
      const { getMessaging, getToken, onMessage } = await import('firebase/messaging');

      let app;
      if (getApps().length === 0) {
        app = initializeApp(FIREBASE_CONFIG);
      } else {
        app = getApps()[0];
      }

      const messaging = getMessaging(app);
      messagingRef.current = messaging;

      // Register service worker (handles background notifications)
      const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey:          VAPID_KEY,
        serviceWorkerRegistration: swReg
      });

      if (token && token !== registeredToken.current) {
        registeredToken.current = token;
        // Register with backend
        await paymentAPI.registerFcmToken({ token, platform: 'web' });
        console.log('📱 Push notifications enabled');
      }

      // Handle foreground messages (app is open)
      onMessage(messaging, (payload) => {
        const { title, body } = payload.notification || {};
        const clickUrl = payload.data?.clickUrl;

        // Use browser Notification API for foreground messages
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && title) {
          const notif = new Notification(title, {
            body,
            icon:  '/logo192.png',
            badge: '/badge.png',
            tag:   payload.data?.orderId || 'warbadobe',
            data:  { url: clickUrl }
          });
          notif.onclick = () => {
            window.focus();
            if (clickUrl) window.location.pathname = clickUrl;
            notif.close();
          };
        }
      });

    } catch (err) {
      // Permission denied or Firebase not set up — fail silently
      if (err.code !== 'messaging/permission-blocked') {
        console.warn('Push notifications unavailable:', err.message);
      }
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!isFirebaseConfigured()) return false;
    if (!('Notification' in window)) return false;
    try { void Notification.permission; } catch { return false; } // Safari guard

    const perm = await Notification.requestPermission();
    if (perm === 'granted') {
      await initFirebase();
      return true;
    }
    return false;
  }, [initFirebase]);

  const unregisterDevice = useCallback(async () => {
    if (!registeredToken.current) return;
    try {
      await paymentAPI.removeFcmToken({ token: registeredToken.current });
      registeredToken.current = null;
    } catch {}
  }, []);

  // Auto-init when user logs in (if permission already granted)
  useEffect(() => {
    if (user && typeof Notification !== 'undefined' && (Notification.permission === 'granted')) {
      initFirebase();
    }
    if (!user && registeredToken.current) {
      // Don't unregister on logout here — the backend handles it via logout route
      registeredToken.current = null;
      messagingRef.current    = null;
    }
  }, [user, initFirebase]);

  return (
    <NotificationContext.Provider value={{ requestPermission, unregisterDevice, isFirebaseConfigured: isFirebaseConfigured() }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be inside NotificationProvider');
  return ctx;
};